import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Container } from "../../styles/Layout";
import {
  FaArrowRight,
  FaPlay,
  FaCode,
  FaRocket,
  FaMobile,
  FaCloud,
  FaStar,
  FaCheck,
  FaBrain,
} from "react-icons/fa";

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    var(--primary-dark) 0%,
    var(--primary) 50%,
    var(--secondary) 100%
  );

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
        circle at 25% 25%,
        rgba(0, 212, 255, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 75% 75%,
        rgba(255, 0, 128, 0.1) 0%,
        transparent 50%
      );
    z-index: 1;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  align-items: center;
  min-height: 80vh;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
    text-align: center;
    min-height: auto;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 600px;

  @media (max-width: 968px) {
    max-width: 100%;
    order: 2;
  }
`;

const HeroVisual = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 968px) {
    order: 1;
    margin-bottom: var(--spacing-xl);
  }
`;

const CompanyBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(0, 212, 255, 0.1) 0%,
    rgba(255, 0, 128, 0.1) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  // margin-bottom: var(--spacing-lg);
  font-size: 0.9rem;
  color: var(--neon-blue);
  font-weight: 600;

  .badge-icon {
    margin-right: 8px;
    color: var(--neon-green);
  }
`;

const MainHeading = styled(motion.h1)`
  font-size: clamp(3rem, 6vw, 5rem);
  line-height: 1.1;
  margin-top: 0;
  margin-bottom: var(--spacing-md);
  font-weight: 900;
  letter-spacing: -0.02em;

  .primary-text {
    color: var(--text-primary);
    display: block;
  }

  .highlight-text {
    background: linear-gradient(
      135deg,
      var(--neon-blue) 0%,
      var(--neon-magenta) 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(
        135deg,
        var(--neon-blue) 0%,
        var(--neon-magenta) 100%
      );
      border-radius: 2px;
      opacity: 0.7;
    }
  }
`;

const SubHeading = styled(motion.p)`
  font-size: clamp(1.2rem, 2.5vw, 1.5rem);
  margin-bottom: var(--spacing-xl);
  color: var(--text-secondary);
  line-height: 1.7;
  max-width: 500px;
`;

const FeatureList = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  font-size: 1rem;

  .check-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--neon-green);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: var(--spacing-md);
    font-size: 0.7rem;
    flex-shrink: 0;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: var(--spacing-lg);
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    var(--neon-blue) 0%,
    var(--neon-magenta) 100%
  );
  color: white;
  padding: 1.3rem 2.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 212, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.6s;
  }

  &:hover {
    box-shadow: 0 15px 50px rgba(0, 212, 255, 0.5);
    transform: translateY(-3px);

    &::before {
      left: 100%;
    }

    svg {
      transform: translateX(5px);
    }
  }

  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  padding: 1.3rem 0;
  transition: all 0.3s ease;

  &:hover {
    color: var(--neon-blue);
    text-shadow: 0 0 10px currentColor;

    .play-button {
      background: var(--neon-blue);
      box-shadow: 0 0 25px var(--neon-blue);
      transform: scale(1.1);
    }
  }

  .play-button {
    width: 55px;
    height: 55px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.05) 100%
    );
    backdrop-filter: blur(12px);
    border: 2px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: var(--spacing-md);
    transition: all 0.3s ease;

    svg {
      margin-left: 3px;
      font-size: 1.2rem;
    }
  }
`;

const VisualContainer = styled.div`
  position: relative;
  width: 500px;
  height: 500px;

  @media (max-width: 968px) {
    width: 350px;
    height: 350px;
  }
`;

const MainCard = styled(motion.div)`
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 350px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 968px) {
    width: 240px;
    height: 300px;
    padding: var(--spacing-lg);
  }
`;

const LogoContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    var(--neon-blue) 0%,
    var(--neon-magenta) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.4);
  font-size: 2.5rem;
  color: white;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const CardSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: var(--spacing-lg);
`;

const TechBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  justify-content: center;
`;

const TechBadge = styled.span`
  background: linear-gradient(
    135deg,
    ${(props) => props.color}20 0%,
    ${(props) => props.color}10 100%
  );
  border: 1px solid ${(props) => props.color}40;
  color: ${(props) => props.color};
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const FloatingCard = styled(motion.div)`
  position: absolute;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${(props) => props.iconColor || "var(--neon-blue)"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
  }

  .content {
    .title {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 2px;
    }

    .subtitle {
      font-size: 0.7rem;
      color: var(--text-muted);
    }
  }
`;

const StatsSection = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xxxl);
  flex-wrap: wrap;

  @media (max-width: 968px) {
    margin-top: var(--spacing-xl);
    gap: var(--spacing-lg);
  }
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: var(--spacing-lg);
  text-align: center;
  min-width: 140px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.08) 100%
    );
    transform: translateY(-5px);
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
  }

  .number {
    font-size: 2.5rem;
    font-weight: 900;
    background: linear-gradient(
      135deg,
      var(--neon-green) 0%,
      var(--neon-blue) 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: block;
    line-height: 1;
  }

  .label {
    font-size: 1rem;
    color: var(--text-secondary);
    margin-top: var(--spacing-sm);
    font-weight: 600;
  }
`;

const Hero = () => {
  const features = [
    "Premium Design & User Experience",
    "Cutting-edge Technology Stack",
    "Scalable & Secure Solutions",
    "24/7 Support & Maintenance",
  ];

  const floatingCards = [
    {
      top: "-15%",
      left: "5%",
      icon: <FaCode />,
      iconColor: "var(--neon-blue)",
      title: "Clean Code",
      subtitle: "Best Practices",
    },
    {
      top: "-10%",
      right: "8%",
      icon: <FaRocket />,
      iconColor: "var(--neon-magenta)",
      title: "Fast Deploy",
      subtitle: "Quick Launch",
    },
    {
      bottom: "60%",
      left: "8%",
      icon: <FaMobile />,
      iconColor: "var(--neon-green)",
      title: "Mobile First",
      subtitle: "Responsive",
    },
  ];

  return (
    <HeroSection id="home">
      <Container>
        <HeroGrid>
          {/* <CompanyBadge
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaStar className="badge-icon" />
            Leading Digital Innovation Company
          </CompanyBadge> */}

          <MainHeading
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{
              gridColumn: "1 / -1",
            }}
          >
            <span className="primary-text">
              Building <span className="highlight-text">Digital Solutions</span>
            </span>
            <span
              className="primary-text"
              style={{ display: "block", marginTop: "0.2em" }}
            >
              for Tomorrow
            </span>
          </MainHeading>
          {/* Place heading above both columns by spanning grid */}
          <HeroContent
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <SubHeading
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              We create innovative web applications, mobile apps, and enterprise
              solutions that transform businesses and drive growth in the
              digital age.
            </SubHeading>

            <FeatureList
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {features.map((feature, index) => (
                <FeatureItem key={index}>
                  <div className="check-icon">
                    <FaCheck />
                  </div>
                  {feature}
                </FeatureItem>
              ))}
            </FeatureList>

            <ButtonGroup
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <PrimaryButton
                href="#contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Project <FaArrowRight />
              </PrimaryButton>

              <SecondaryButton
                href="#about"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="play-button">
                  <FaPlay />
                </div>
                Watch Our Work
              </SecondaryButton>
            </ButtonGroup>
          </HeroContent>

          <HeroVisual
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <VisualContainer>
              <MainCard
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <LogoContainer>
                  <FaBrain />
                </LogoContainer>

                <CardTitle>GeoX Labs</CardTitle>
                <CardSubtitle>
                  Advanced AI solutions with machine learning expertise
                </CardSubtitle>

                <TechBadges>
                  <TechBadge color="var(--neon-blue)">TensorFlow</TechBadge>
                  <TechBadge color="var(--neon-green)">PyTorch</TechBadge>
                  <TechBadge color="var(--neon-magenta)">OpenAI</TechBadge>
                  <TechBadge color="var(--neon-yellow)">Hugging Face</TechBadge>
                </TechBadges>
              </MainCard>

              {floatingCards.map((card, index) => (
                <FloatingCard
                  key={index}
                  iconColor={card.iconColor}
                  style={{
                    top: card.top,
                    bottom: card.bottom,
                    left: card.left,
                    right: card.right,
                  }}
                  animate={{
                    y: [0, -15, 0],
                    rotate: [0, 2, -2, 0],
                  }}
                  transition={{
                    duration: 3 + index * 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.2,
                  }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="icon">{card.icon}</div>
                  <div className="content">
                    <div className="title">{card.title}</div>
                    <div className="subtitle">{card.subtitle}</div>
                  </div>
                </FloatingCard>
              ))}
            </VisualContainer>
          </HeroVisual>
        </HeroGrid>

        <StatsSection
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <StatCard
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
          >
            <span className="number">50+</span>
            <span className="label">Projects</span>
          </StatCard>
          <StatCard
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.3 }}
          >
            <span className="number">25+</span>
            <span className="label">Clients</span>
          </StatCard>
          <StatCard
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 }}
          >
            <span className="number">5+</span>
            <span className="label">Years</span>
          </StatCard>
          <StatCard
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.5 }}
          >
            <span className="number">100%</span>
            <span className="label">Success</span>
          </StatCard>
        </StatsSection>
      </Container>
    </HeroSection>
  );
};

export default Hero;
